import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import { assertStatus } from "@mittwald/api-client-NEXT";
import { checkForValidationErrors, ValidationError } from "../../../base";
import type { ProjectInviteBehaviors } from "./types.js";

export const apiProjectInviteBehaviors = (
  client: MittwaldAPIV2Client,
): ProjectInviteBehaviors => ({
  find: async (projectInviteId) => {
    const response = await client.project.getProjectInvite({
      projectInviteId,
    });
    if (response.status === 200) {
      return response.data;
    }
    assertStatus(response, 404);
  },

  list: async (projectId, query) => {
    const response = await client.project.listInvitesForProject({
      projectId,
      queryParameters: query,
    });

    assertStatus(response, 200);

    return {
      items: response.data,
    };
  },

  delete: async (projectInviteId) => {
    const response = await client.project.deleteProjectInvite({
      projectInviteId,
    });
    assertStatus(response, 204);
  },

  create: async (projectId, data) => {
    const response = await client.project.createProjectInvite({
      projectId,
      data,
    });
    if (response.status === 409) {
      const message =
        response.data.message && typeof response.data.message === "string"
          ? response.data.message
          : undefined;

      if (message?.includes("already exists")) {
        return [
          new ValidationError({
            message: "inviteAlreadyExists",
            type: "inviteAlreadyExists",
            path: "mailAddress",
          }),
        ];
      }
      if (message?.includes("already member")) {
        return [
          new ValidationError({
            message: "alreadyMember",
            type: "alreadyMember",
            path: "mailAddress",
          }),
        ];
      }
    }

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 201);
    return response.data;
  },

  getByToken: async (invitationToken) => {
    const response = await client.project.getProjectTokenInvite({
      headers: { token: invitationToken },
    });
    assertStatus(response, 200);

    return response.data;
  },

  accept: async (projectInviteId, invitationToken) => {
    const response = await client.project.acceptProjectInvite({
      projectInviteId,
      data: { invitationToken },
    });
    assertStatus(response, 204);
  },

  decline: async (projectInviteId) => {
    const response = await client.project.declineProjectInvite({
      projectInviteId,
    });
    assertStatus(response, 204);
  },

  listIncoming: async (query) => {
    const response = await client.project.listProjectInvites({
      queryParameters: query,
    });

    assertStatus(response, 200);

    return {
      items: response.data,
    };
  },
});
